import { Button, Group, Loader, Progress, SegmentedControl, Select, Stack, TextInput, Title } from "@mantine/core";
import { useDebouncedState, useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Suspense, useEffect, useState } from "react";
import { Content } from "shared/components/global/Content";
import { GroupChildrenRepository, GroupSearchRepository } from "shared/components/organization/groups/data";
import { GroupFilterTable } from "shared/components/organization/groups/GroupFilterTable";
import { GroupSearchTable } from "shared/components/organization/groups/GroupSearchTable";
import { groupsQueryOptions } from "shared/graphql/groups";
import { notify } from "shared/utils/notify";
import { container } from "tsyringe";
import { z } from "zod";

const Page = () => {
    const nav = useNavigate();
    const search = Route.useSearch();

    const [filterMode, { toggle: toggleFilterMode, open: enableFilterMode }] = useDisclosure(!!search.parent);
    const [groupSearch, setGroupSearch] = useDebouncedState("", 300);
    const [groupFilter, setGroupFilter] = useState("");
    const [debouncedGroupFilter] = useDebouncedValue(groupFilter, 300);
    const [selectedGroupFilter, setSelectedGroupFilter] = useState<{ id: string; name: string }>();

    // query to populate the filter by parent dropdown
    const { data: groupFilterSearchResults } = useQuery({
        ...groupsQueryOptions.search(debouncedGroupFilter),
        queryKey: ["group", "search", "filter", debouncedGroupFilter],
        enabled: filterMode,
    });

    // query to populate the table after filtering by a parent
    const groupChildrenRepo = container.resolve(GroupChildrenRepository);
    const groupChildrenLoader = groupChildrenRepo.get({ parentId: selectedGroupFilter?.id ?? "", queryMembership: true });
    const { isLoading: groupChildrenIsLoading } = groupChildrenLoader.useStatus();

    // default table query, optionally filtered by a name search
    const groupSearchRepo = container.resolve(GroupSearchRepository);
    const groupSearchLoader = groupSearchRepo.get({ query: groupSearch, queryMembership: true });
    const { isLoading: groupSearchIsLoading } = groupSearchLoader.useStatus();

    function getFilterData() {
        const base = groupFilterSearchResults?.map((it) => ({ label: it.name, value: it.id }));
        if (filterMode && !!selectedGroupFilter && !groupFilterSearchResults?.some((it) => it.id == selectedGroupFilter.id)) {
            return base?.concat({ label: selectedGroupFilter.name, value: selectedGroupFilter.id });
        }
        return base;
    }

    useEffect(() => {
        if (!!search.parent) {
            enableFilterMode();
        }
        setGroupFilter("");
        setSelectedGroupFilter(search.parent);
    }, [search.parent]);

    return (
        <Content paper style={{ border: "none" }}>
            <Outlet />
            <Content.Heading>
                <Title order={3} size="h1">
                    Groups
                </Title>
            </Content.Heading>
            <Stack gap={0}>
                <Group justify="space-between" align="flex-end" pb="sm">
                    <Group align="flex-end">
                        <SegmentedControl
                            mt="sm"
                            data={["Search", "Filter"]}
                            value={filterMode ? "Filter" : "Search"}
                            onChange={() => {
                                setGroupSearch("");
                                setGroupFilter("");
                                setSelectedGroupFilter(undefined);
                                toggleFilterMode();
                            }}
                        />
                        {filterMode ? (
                            <Select
                                label="Children of"
                                placeholder="Filter groups..."
                                searchable
                                clearable
                                searchValue={groupFilter}
                                onSearchChange={setGroupFilter}
                                onChange={(id, opt) => {
                                    nav({ to: ".", search: { parent: id == null ? undefined : { id: opt.value, name: opt.label } } }).catch(
                                        notify.catch,
                                    );
                                }}
                                style={{ flexGrow: 1 }}
                                maw={300}
                                value={selectedGroupFilter?.id || null}
                                data={getFilterData()}
                            />
                        ) : (
                            <TextInput
                                label="Search Groups"
                                placeholder="Search groups..."
                                style={{ flexGrow: 1 }}
                                maw={300}
                                onChange={(e) => setGroupSearch(e.currentTarget.value)}
                            />
                        )}
                    </Group>
                    <Link to="/organization/groups/create">
                        <Button variant="default" leftSection={<IconPlus size={16} />}>
                            Create
                        </Button>
                    </Link>
                </Group>
                {!!selectedGroupFilter?.id ? (
                    <GroupFilterTable selectedGroupFilterId={selectedGroupFilter.id} />
                ) : (
                    <GroupSearchTable groupSearch={groupSearch} />
                )}
                {(groupSearchIsLoading || (!!selectedGroupFilter?.id && groupChildrenIsLoading)) && (
                    <Progress value={100} striped animated />
                )}
            </Stack>
        </Content>
    );
};

const searchSchema = z.object({
    parent: z
        .object({
            id: z.string(),
            name: z.string(),
        })
        .optional(),
});

/** @public */
export const Route = createFileRoute("/_auth/organization/groups")({
    component: () => (
        <Suspense fallback={<Loader />}>
            <Page />
        </Suspense>
    ),
    validateSearch: zodValidator(searchSchema),
});
